import React, { useState, useEffect } from 'react';
import { motion as Motion, AnimatePresence } from 'framer-motion';
import { siteData } from '../data';
import { FacebookFeed } from '../components/FacebookFeed';
import { ArrowRight, Utensils, Building2, Leaf, Check } from 'lucide-react';
import { Link } from 'react-router-dom';

const HeroCarousel = () => {
  const images = [
    "https://assets.auxodev.dk/assets/eaff47c2-4c55-4355-8653-21b1b19ca1e6/d8624714-f0c2-41ca-85dd-25e575eb5596.jpg", // Vietnamese bowl
    "https://assets.auxodev.dk/assets/eaff47c2-4c55-4355-8653-21b1b19ca1e6/3bd9acc9-c7ee-4241-aa44-5716919f118f.jpg", // Banh Mi variety
    "https://assets.auxodev.dk/assets/eaff47c2-4c55-4355-8653-21b1b19ca1e6/7031f56f-b22d-43e1-83a8-2e85008fdd45.jpg", // Banh Mi close-up
    "https://assets.auxodev.dk/assets/eaff47c2-4c55-4355-8653-21b1b19ca1e6/617de86c-2eba-4d54-8914-aaea158e276d.jpg"  // Summer rolls
  ];

  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % images.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [images.length]);

  return (
    <div className="absolute inset-0 z-0 bg-zinc-900">
      <AnimatePresence initial={false}>
        <Motion.div
          key={currentIndex}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 1 }}
          transition={{ duration: 1.5, ease: "easeInOut" }}
          className="absolute inset-0"
        >
          <img
            src={images[currentIndex]}
            className="w-full h-full object-cover"
            alt="Hero background food"
          />
          <div className="absolute inset-0 bg-black/40" />
        </Motion.div>
      </AnimatePresence>
    </div>
  );
};

const Home = () => {
  return (
    <div className="flex flex-col">
      {/* Hero Section */}
      <section className="relative min-h-[80vh] flex items-center justify-center overflow-hidden">
        <HeroCarousel />
        <div className="relative z-10 text-center text-white px-4 max-w-6xl">
          <Motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-3xl sm:text-5xl md:text-7xl font-black mb-8 uppercase tracking-tighter"
          >
            {siteData.hero.title}
          </Motion.h1>
          <Motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg sm:text-2xl md:text-3xl text-zinc-200 mb-12 max-w-3xl mx-auto leading-relaxed"
          >
            {siteData.hero.subtitle}
          </Motion.p>
          <Motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <Link to="/menukort" className="bg-red-600 border-2 border-red-600 text-white px-8 py-4 rounded-full font-bold hover:bg-red-700 hover:-translate-y-1 transition-all duration-300 flex items-center justify-center gap-2 shadow-lg shadow-red-600/20">
              Se Menukort <ArrowRight className="h-5 w-5" />
            </Link>
            <a 
              href={siteData.company.orderUrl} 
              target="_blank" 
              rel="noopener noreferrer" 
              className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-full font-bold hover:bg-white hover:text-zinc-950 hover:-translate-y-1 transition-all duration-300 text-center"
            >
              Bestil Online
            </a>
          </Motion.div>
        </div>
      </section>

      {/* Quick Links / Features */}
      <section className="py-12 bg-zinc-900">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <Motion.div 
              className="text-center"
            >
              <div className="flex items-center justify-center mx-auto mb-6">
                <Utensils className="h-12 w-12 text-white" />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-white uppercase tracking-wide">Vietnamesisk Menu</h3>
              <p className="text-zinc-400 mb-6">Oplev autentiske retter fra det vietnamesiske gadekøkken, tilberedt med passion.</p>
              <Link to="/menukort" className="text-white font-bold hover:text-zinc-300 inline-flex items-center gap-1 uppercase tracking-wider text-sm">
                Se menukort <ArrowRight className="h-4 w-4" />
              </Link>
            </Motion.div>

            <Motion.div 
              className="text-center"
            >
              <div className="flex items-center justify-center mx-auto mb-6">
                <Building2 className="h-12 w-12 text-white" />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-white uppercase tracking-wide">Erhvervsordning</h3>
              <p className="text-zinc-400 mb-6">Sund og eksotisk vietnamesisk frokost leveret direkte til din virksomhed.</p>
              <Link to="/frokostordning" className="text-white font-bold hover:text-zinc-300 inline-flex items-center gap-1 uppercase tracking-wider text-sm">
                Læs mere <ArrowRight className="h-4 w-4" />
              </Link>
            </Motion.div>

            <Motion.div 
              className="text-center"
            >
              <div className="flex items-center justify-center mx-auto mb-6">
                <Leaf className="h-12 w-12 text-white" />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-white uppercase tracking-wide">Madkultur</h3>
              <p className="text-zinc-400 mb-6">Vi går aldrig på kompromis med kvaliteten og respekten for naturens råvarer.</p>
              <Link to="/madkultur" className="text-white font-bold hover:text-zinc-300 inline-flex items-center gap-1 uppercase tracking-wider text-sm">
                Læs mere <ArrowRight className="h-4 w-4" />
              </Link>
            </Motion.div>
          </div>
        </div>
      </section>

      {/* Menukort Section */}
      <section className="bg-zinc-950 overflow-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-2 items-center">
          <Motion.div 
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="flex flex-col gap-6 py-16 px-6 lg:px-24"
          >
            <span className="text-white font-bold uppercase tracking-widest text-sm">Vores Udvalg</span>
            <h2 className="text-4xl md:text-5xl font-bold text-white leading-tight uppercase tracking-tight">
              {siteData.menu.title}
            </h2>
            <p className="text-xl text-zinc-300">
              {siteData.menu.description}
            </p>
            <div className="space-y-4 text-zinc-400 text-lg leading-relaxed">
              <p>
                Gå på opdagelse i vores autentiske udvalg af vietnamesiske specialiteter. Fra vores berømte, sprøde Banh Mi baguetter til vores farverige og nærende bowls. 
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4">
                <div className="border-l-4 border-red-600 pl-4">
                  <h4 className="font-bold text-white uppercase tracking-wide">Banh Mi</h4>
                  <p className="text-sm">Vores signatur-sandwich med friske urter.</p>
                </div>
                <div className="border-l-4 border-red-600 pl-4">
                  <h4 className="font-bold text-white uppercase tracking-wide">Bowls</h4>
                  <p className="text-sm">Friske risnudler med masser af grønt.</p>
                </div>
              </div>
            </div>
            <div className="pt-6">
              <Link to="/menukort" className="inline-flex items-center gap-2 bg-red-600 border-2 border-red-600 text-white px-8 py-4 rounded-full font-bold hover:bg-red-700 hover:border-red-700 hover:-translate-y-1 transition-all duration-300 shadow-lg shadow-red-600/20">
                Se det fulde menukort <ArrowRight className="h-5 w-5" />
              </Link>
            </div>
          </Motion.div>

          <Motion.div 
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative h-[500px] lg:h-[700px] overflow-hidden"
          >
            <img 
              src="https://assets.auxodev.dk/assets/eaff47c2-4c55-4355-8653-21b1b19ca1e6/7031f56f-b22d-43e1-83a8-2e85008fdd45.jpg" 
              alt="Banh Mi close up" 
              className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
            />
          </Motion.div>
        </div>
      </section>

      {/* Frokostordning Section */}
      <section className="bg-zinc-100 text-zinc-900 overflow-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-2 items-center">
          <Motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative h-[500px] lg:h-[700px] overflow-hidden order-2 lg:order-1"
          >
            <img 
              src="https://assets.auxodev.dk/assets/eaff47c2-4c55-4355-8653-21b1b19ca1e6/9912f99c-d044-4ee6-942e-bd56dce9b637.jpg" 
              alt="Vietnamesisk frokostordning" 
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent"></div>
          </Motion.div>

          <Motion.div 
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="flex flex-col gap-6 py-16 px-6 lg:px-24 order-1 lg:order-2"
          >
            <span className="text-zinc-500 font-bold uppercase tracking-widest text-sm">Til Virksomheder</span>
            <h2 className="text-3xl md:text-5xl font-bold leading-tight uppercase tracking-tight text-zinc-900">
              {siteData.lunch.title}
            </h2>
            <p className="text-xl text-zinc-600">
              {siteData.lunch.description}
            </p>
            <div className="space-y-6 text-zinc-600 text-lg leading-relaxed">
              <p>
                {siteData.lunch.content}
              </p>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {siteData.lunch.features.map((feature, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <Check className="h-5 w-5 text-black shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
            <div className="pt-6">
              <Link to="/frokostordning" className="inline-flex items-center gap-2 bg-transparent border-2 border-zinc-900 text-zinc-900 px-8 py-4 rounded-full font-bold hover:bg-black/5 hover:-translate-y-1 transition-all duration-300 shadow-lg">
                Læs mere om erhvervsaftaler <ArrowRight className="h-5 w-5" />
              </Link>
            </div>
          </Motion.div>
        </div>
      </section>

      {/* Madkultur Section (2-column layout) */}
      <section className="bg-zinc-900 overflow-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-2 items-center">
          <Motion.div 
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="flex flex-col gap-6 py-16 px-6 lg:px-24"
          >
            <span className="text-white font-bold uppercase tracking-widest text-sm">Vores Passion</span>
            <h2 className="text-4xl md:text-5xl font-bold text-white leading-tight uppercase tracking-tight">
              {siteData.culture.title}
            </h2>
            <p className="text-xl text-zinc-300 font-medium italic">
              "{siteData.culture.description}"
            </p>
            <div className="space-y-4 text-zinc-400 text-lg leading-relaxed">
              <p>
                {siteData.culture.content}
              </p>
              <p>
                Vi tror på, at hemmeligheden bag den perfekte Banh Mi ligger i balancen mellem de fem grundsmage: sødt, surt, salt, bittert og stærkt. Hver ingrediens er nøje udvalgt for at skabe en symfoni af smagsoplevelser.
              </p>
            </div>
            <div className="pt-6">
              <Link to="/madkultur" className="inline-flex items-center gap-2 bg-transparent border-2 border-white text-white px-8 py-4 rounded-full font-bold hover:bg-white/10 hover:-translate-y-1 transition-all duration-300 shadow-lg">
                Læs mere om vores kultur <ArrowRight className="h-5 w-5" />
              </Link>
            </div>
          </Motion.div>

          <Motion.div 
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative h-[500px] lg:h-[700px] overflow-hidden"
          >
            <img 
              src="https://assets.auxodev.dk/assets/eaff47c2-4c55-4355-8653-21b1b19ca1e6/df842dad-45fc-40a3-afa2-5d020d63cd07.png" 
              alt="Friske vietnamesiske ingredienser" 
              className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
          </Motion.div>
        </div>
      </section>

      <FacebookFeed />
    </div>
  );
};

export default Home;
